import React from "react";
import PropTypes from "prop-types";

/**
 * EntityHeader — the presentational header shell for entity-shaped profiles
 * (Company, PR-firm, Product, Media Outlet). A full-bleed cover band, an avatar column that
 * overlaps it, a main identity column, an optional top-right actions row, and an optional
 * trailing aside.
 *
 * The entity-shaped sibling of `<PersonHeader>` — same chassis: it renders onto the very same
 * `.person-header-card` / `.person-header-banner` / `.person-header-actions` classes (see
 * `person_profiles.scss`, which documents both shells' shared use) rather than carrying its own
 * copy of the recipe. `aside` is the one slot `<PersonHeader>` doesn't have: entity pages still
 * show a trailing badge column (PR Agency, visibility) beside identity, so it stays a shell-level
 * slot instead of folding into `identity` on every engine. Like `<PersonHeader>` this is a layout
 * shell only — it holds NO engine logic; the engine passes already-built slots.
 *
 * (Person-shaped engines — Expert, PR, Media Profile — use the separate `<PersonHeader>`. The
 * header is chosen by a person-shaped trait, not by model class.)
 *
 * @param {Object} props
 * @param {React.ReactNode} props.avatar - Avatar/logo slot
 * @param {React.ReactNode} props.identity - Main identity column (name, headline, links)
 * @param {React.ReactNode} [props.actions] - Optional top-right actions row (owner/viewer controls)
 * @param {React.ReactNode} [props.aside] - Optional trailing column (badges)
 * @param {React.ReactNode} [props.banner] - Cover/banner band slot (rendered only when provided)
 * @param {string} [props.avatarWrapperClassName] - Extra classes for the avatar column (engine CSS hook,
 *   e.g. Company's `company-logo-container` overlap styling)
 */
function EntityHeader({ avatar, identity, actions = null, aside = null, banner = null, avatarWrapperClassName = "" }) {
  const hasBanner = banner != null;

  return (
    <div
      className={`person-header-card${hasBanner ? " person-header-card--with-banner" : ""} animated faster fadeIn`}
      style={{ position: "relative" }}
    >
      {banner}

      <div className="person-header-content p-4">
        <div className="row">
          <div
            className={`col-12 col-sm-auto d-flex justify-content-center mb-3 mb-sm-0 ${avatarWrapperClassName}`.trim()}
          >
            {avatar}
          </div>

          <div className="col">
            {/* The actions (and the aside badges under them) sit right of the identity while it keeps
                ~24rem, then wrap under it. */}
            <div className="person-header-main d-flex flex-wrap align-items-start">
              <div className="person-header-identity">{identity}</div>
              {(actions != null || aside != null) && (
                <div className="person-header-actions d-flex flex-column align-items-end gap-2">
                  {actions != null && <div className="d-flex flex-wrap gap-2">{actions}</div>}
                  {aside}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

EntityHeader.propTypes = {
  avatar: PropTypes.node.isRequired,
  identity: PropTypes.node.isRequired,
  actions: PropTypes.node,
  aside: PropTypes.node,
  banner: PropTypes.node,
  avatarWrapperClassName: PropTypes.string,
};

export default EntityHeader;
